<script setup lang="ts">
import { watch, ref, nextTick, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import LanguageSelector from '@/components/layout/LanguageSelector.vue'

const props = defineProps<{
  open: boolean
  navItems: Array<{ id: string; key: string }>
  activeSection: string
}>()

const emit = defineEmits<{
  close: []
  navigate: [id: string]
}>()

const { t } = useI18n()
const closeButton = ref<HTMLButtonElement | null>(null)
const menuPanel = ref<HTMLDivElement | null>(null)

watch(() => props.open, async (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden'
    await nextTick()
    closeButton.value?.focus()
  } else {
    document.body.style.overflow = ''
  }
})

onUnmounted(() => {
  document.body.style.overflow = ''
})

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    emit('close')
  }
  if (e.key === 'Tab' && menuPanel.value) {
    const focusable = menuPanel.value.querySelectorAll<HTMLElement>(
      'button, a, [tabindex]:not([tabindex="-1"])'
    )
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
        @click="emit('close')"
        @keydown="onKeydown"
      >
        <Transition
          enter-active-class="transition-transform duration-300 ease-out"
          enter-from-class="translate-x-full rtl:-translate-x-full"
          enter-to-class="translate-x-0"
          leave-active-class="transition-transform duration-200 ease-in"
          leave-from-class="translate-x-0"
          leave-to-class="translate-x-full rtl:-translate-x-full"
        >
          <div
            v-if="open"
            ref="menuPanel"
            role="dialog"
            :aria-label="t('nav.guide')"
            aria-modal="true"
            class="absolute top-0 end-0 h-full w-80 max-w-[85vw] bg-white dark:bg-slate-900 shadow-glass-xl overflow-y-auto"
            @click.stop
          >
            <div class="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
              <span class="text-lg font-semibold text-text dark:text-dark-text">
                {{ t('nav.guide') }}
              </span>
              <button
                ref="closeButton"
                type="button"
                class="p-2 rounded-lg text-muted hover:text-text hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                :aria-label="t('nav.close_menu')"
                @click="emit('close')"
              >
                <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <nav class="p-4 space-y-1">
              <button
                v-for="item in navItems"
                :key="item.id"
                type="button"
                class="w-full text-start px-4 py-3 rounded-xl text-base font-medium transition-colors"
                :class="[
                  activeSection === item.id
                    ? 'text-primary bg-primary-soft dark:bg-primary/20'
                    : 'text-text dark:text-dark-text hover:bg-slate-100 dark:hover:bg-slate-800',
                ]"
                @click="emit('navigate', item.id)"
              >
                {{ t(item.key) }}
              </button>
            </nav>

            <div class="p-4 border-t border-slate-200 dark:border-slate-700">
              <LanguageSelector />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
